/**
 * Paste cleaner — makes text copied from a PDF, Word file, web page or WhatsApp
 * safe to paste into any ActEaze text box.
 *
 * Why this exists
 * ---------------
 * Copied text often carries characters you cannot see (zero-width spaces, a
 * byte-order mark, soft hyphens, non-breaking spaces, line breaks inside one
 * line). The box looks right, but:
 *   - the hidden characters use up the box's maxlength, so the browser silently
 *     cuts off the END of the pasted text (a GSTIN loses its last letters);
 *   - "29 ABCDE 1234 F1Z5" pasted into the 15-letter GSTIN box keeps only
 *     "29 ABCDE 1234 F", and the PAN worked out from it is wrong;
 *   - the server then rejects a value that looks correct on screen.
 *
 * What it does, for every text box and textarea on every page:
 *   1. Cleans the pasted text (see textClean.ts) BEFORE it goes into the box.
 *      Boxes marked `data-paste="code"` (GSTIN, PAN, TAN, PIN code) also lose
 *      every space, hyphen, dot and slash and are upper-cased.
 *   2. If the cleaned text is still longer than the box allows, it keeps what
 *      fits and tells the user, instead of cutting it off without a word.
 *   3. If hidden characters were removed, it says so in a small message.
 *
 * Pastes that need no change are left to the browser. A page handler that has
 * already taken the paste (e.preventDefault, e.g. a grid that pastes many cells)
 * is respected. Password boxes and `data-paste="raw"` boxes are never touched.
 */
import { cleanCode, cleanText } from './textClean';
import { showToast } from './toast';

declare global {
  interface Window {
    __acteazePasteCleaner?: boolean;
  }
}

const TEXT_TYPES = new Set(['text', 'search', 'tel', 'email', 'url', '']);
// These input types do not support selectionStart / setRangeText.
const NO_SELECTION_TYPES = new Set(['email', 'url']);

function onPaste(e: ClipboardEvent): void {
  if (e.defaultPrevented) return;
  const el = e.target;
  const isArea = el instanceof HTMLTextAreaElement;
  if (!isArea && !(el instanceof HTMLInputElement)) return;
  if (!isArea && !TEXT_TYPES.has((el as HTMLInputElement).type)) return;
  if (el.readOnly || el.disabled || el.dataset.paste === 'raw') return;

  const raw = e.clipboardData?.getData('text/plain');
  if (!raw) return;

  const codeMode = el.dataset.paste === 'code';
  const result = codeMode ? cleanCode(raw) : cleanText(raw, isArea);
  let text = result.text;

  const hasSelection = isArea || !NO_SELECTION_TYPES.has((el as HTMLInputElement).type);
  const start = hasSelection ? el.selectionStart ?? el.value.length : el.value.length;
  const end = hasSelection ? el.selectionEnd ?? start : start;
  // Replacing the whole box (or pasting into an empty one): drop the spaces the
  // copy picked up around the value.
  if (!isArea && start === 0 && end === el.value.length) text = text.trim();

  const max = el.maxLength;
  const room = max >= 0 ? Math.max(0, max - (el.value.length - (end - start))) : Infinity;
  let cut = false;
  if (text.length > room) {
    text = text.slice(0, room);
    // Don't leave half of an emoji behind.
    if (/[\uD800-\uDBFF]$/.test(text)) text = text.slice(0, -1);
    cut = true;
  }

  if (text === raw && !cut) return; // nothing to fix — let the browser paste

  e.preventDefault();
  insert(el, text, start, end, hasSelection);

  if (cut) {
    showToast(
      `The copied text is too long for this box. It can have only ${max} characters, so the extra part was removed. Please check it.`,
      { level: 'warning' },
    );
  } else if (result.removedHidden) {
    showToast('Some hidden characters in the copied text were removed.', { level: 'info' });
  }
}

function insert(
  el: HTMLInputElement | HTMLTextAreaElement,
  text: string,
  start: number,
  end: number,
  hasSelection: boolean,
): void {
  // insertText keeps Ctrl+Z working and fires a normal "input" event.
  el.focus();
  if (document.execCommand && document.execCommand('insertText', false, text)) return;

  if (hasSelection) {
    el.setRangeText(text, start, end, 'end');
  } else {
    el.value = el.value + text;
  }
  el.dispatchEvent(new Event('input', { bubbles: true }));
}

if (!window.__acteazePasteCleaner) {
  window.__acteazePasteCleaner = true;
  document.addEventListener('paste', onPaste);
}
